import { ImageIcon, TrashIcon } from "@xola/icons";
import { Logo, SubmitButton } from "@xola/ui-kit";
import clsx from "clsx";
import React, { ReactNode, useRef, useState } from "react";
import { MediaSize, MediaType } from "types";
import { SecondaryButton } from "@/components/Common/SecondaryButton";

interface MediaUploadProps {
    source?: string | null;
    thumbnailSrc?: string | null;
    size?: MediaSize;
    isLoading?: boolean;
    mediaType?: MediaType;
    allowedExtensions?: string[];
    maxSize?: number;
    hasDelete?: boolean;
    caption?: string;
    requirements?: ReactNode | null;
    csvAcceptFormats?: string;
    onChange: (file: File) => void;
    onDelete: () => void;
    onError: (error: string) => void;
}

const EXTENSION_MIME_MAP: Record<string, string> = {
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    png: "image/png",
    gif: "image/gif",
    webp: "image/webp",
    mp4: "video/mp4",
    mkv: "video/x-matroska",
    webm: "video/webm",
    mov: "video/quicktime",
    avi: "video/x-msvideo",
};

function getRequirementsString(mediaType: string, allowedExtensions: string[], maxSize?: number) {
    const extensions =
        allowedExtensions && allowedExtensions.length > 0
            ? allowedExtensions
            : mediaType === "video"
            ? ["mp4", "mkv", "webm"]
            : ["png", "jpg", "jpeg", "gif", "webp"];
    let extensionList = "";
    if (extensions.length === 1) {
        extensionList = extensions[0].toUpperCase();
    } else if (extensions.length === 2) {
        extensionList = `${extensions[0].toUpperCase()} or ${extensions[1].toUpperCase()}`;
    } else {
        extensionList = extensions
            .slice(0, -1)
            .map((extension) => extension.toUpperCase())
            .join(", ");
        extensionList += `, or ${extensions[extensions.length - 1].toUpperCase()}`;
    }
    const typeLabel = mediaType === "video" ? "video" : "image";
    return `Check that the ${typeLabel} is in ${extensionList} format${
        maxSize ? ` and does not exceed ${maxSize}MB` : ""
    }`;
}

function getAcceptString(allowedExtensions: string[], mediaType: string) {
    if (!allowedExtensions || allowedExtensions.length === 0) {
        return mediaType === "video"
            ? "video/mp4,video/x-matroska,video/webm"
            : "image/png,image/jpeg,image/gif,image/webp";
    }
    return allowedExtensions
        .map((extension) => EXTENSION_MIME_MAP[extension.toLowerCase()] || `.${extension.toLowerCase()}`)
        .join(",");
}

export const MediaUpload = ({
    source = "",
    thumbnailSrc: thumbnailSource = "",
    size = "large",
    isLoading = false,
    maxSize = 5,
    hasDelete = true,
    requirements = null,
    caption = "",
    mediaType = "image",
    allowedExtensions = [],
    csvAcceptFormats = "",
    onChange,
    onDelete,
    onError,
    ...inputProps
}: MediaUploadProps) => {
    const inputReference = useRef<HTMLInputElement>(null);
    const [inputKey, setInputKey] = useState(0);

    const handleUploadClick = () => {
        inputReference.current?.click();
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files || files.length === 0) {
            return;
        }
        const file = files[0];
        if (maxSize && 1_048_576 * maxSize < file.size) {
            onError(`Image size shouldn't exceed ${maxSize}MB`);
            return;
        }

        onChange(file);
    };

    const handleDelete = () => {
        // while deleting the input should be emptied - this increment cause input to be replaced for a new one
        setInputKey(inputKey + 1);
        onDelete();
    };

    const hasRequirements = typeof requirements === "string" ? requirements?.trim().length > 0 : !!requirements;
    const hasCaption = caption ? caption.trim().length > 0 : false;
    const csvAcceptFormatList = csvAcceptFormats ? csvAcceptFormats : getAcceptString(allowedExtensions, mediaType);
    const mediaTypeName = mediaType === "video" ? "Video" : "Image";
    const requirementString = getRequirementsString(mediaType, allowedExtensions, maxSize);

    return (
        <div className={clsx("flex items-center rounded bg-gray-lighter p-4", hasDelete ? "space-x-2" : "space-x-3")}>
            <div>
                {thumbnailSource ? (
                    <Logo src={thumbnailSource} size={size} />
                ) : (
                    <div
                        className={clsx(
                            Logo.sizes[size],
                            "flex items-center justify-center rounded border border-gray-light",
                        )}
                    >
                        <ImageIcon size="large" className="text-gray" />
                    </div>
                )}
            </div>

            <div className="flex flex-col space-y-2">
                <div className="space-x-2">
                    {hasDelete ? (
                        <>
                            <SecondaryButton icon={<TrashIcon />} disabled={isLoading} onClick={handleDelete}>
                                Delete
                            </SecondaryButton>
                            <SubmitButton disabled={isLoading} isLoading={isLoading} onClick={handleUploadClick}>
                                {hasCaption ? caption.trim() : `Upload New ${mediaTypeName}`}
                            </SubmitButton>
                        </>
                    ) : (
                        <SubmitButton disabled={isLoading} isLoading={isLoading} onClick={handleUploadClick}>
                            {hasCaption
                                ? caption.trim()
                                : source
                                ? `Replace ${mediaTypeName}`
                                : `Upload New ${mediaTypeName}`}
                        </SubmitButton>
                    )}
                </div>
                <input
                    key={inputKey}
                    ref={inputReference}
                    className="hidden"
                    type="file"
                    multiple={false}
                    accept={csvAcceptFormatList}
                    onChange={handleChange}
                    {...inputProps}
                />
                <div className="text-xs text-gray-darker">
                    {hasRequirements ? requirements : <div>{requirementString}</div>}
                </div>
            </div>
        </div>
    );
};
